/**
 * Phase 2L-A115 — Property Manager Add-Managed-Property modal.
 *
 * Lets an approved PM add a basic managed property to their portfolio
 * BEFORE any water test is booked. Submits to
 *   POST /api/property-manager/properties
 * which forces `managed_by_partner_id` + `created_by_partner_id` +
 * `source: "property_manager"` from the PM's token — body cannot
 * override.
 *
 * Address input reuses the existing booking-flow `AddressAutocomplete`
 * widget (Australia-restricted Google Places with manual fallback).
 *
 * Does NOT create a booking, payment, lab test, report, or score.
 * On success the parent dashboard re-fetches the portfolio.
 */
import { useEffect, useState } from "react";
import { X, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { parseAddress } from "@/lib/serviceArea";

const WATER_SOURCES = [
  { value: "rainwater_tank", label: "Rainwater tank" },
  { value: "bore",           label: "Bore" },
  { value: "mixed",          label: "Mixed / Other" },
  { value: "unsure",         label: "Unsure" },
];

const CADENCES = [
  { value: "",          label: "Not sure yet" },
  { value: "annual",    label: "Annual" },
  { value: "biannual",  label: "Every 6 months" },
  { value: "quarterly", label: "Quarterly" },
];

const EMPTY = {
  address: "", suburb: "", postcode: "", water_source: "rainwater_tank",
  owner_name: "", owner_email: "", owner_phone: "",
  tenant_name: "", tenant_email: "", tenant_phone: "",
  notes: "", testing_required_now: false, preferred_retest_cadence: "",
};

export function PmAddPropertyModal({ open, onClose, onCreated, onCreatedAndBook }) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submittingMode, setSubmittingMode] = useState(null); // 'add' | 'add-and-book'
  const [error, setError] = useState(null);

  // Reset form whenever the modal opens
  useEffect(() => {
    if (open) {
      setForm(EMPTY);
      setError(null);
    }
  }, [open]);

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e, mode = "add") => {
    e?.preventDefault?.();
    setError(null);
    const address = (form.address || "").trim();
    if (address.length < 3) {
      setError("Property address is required.");
      return;
    }
    // The address picker now carries the suburb + postcode in the
    // formatted string itself, so the modal no longer surfaces those
    // fields. Derive them at submit-time so the backend idempotency
    // key (`address_key = norm(address + suburb + postcode)`) stays
    // stable and any future address-based lookups still work.
    const derived = parseAddress(address);
    const payload = {
      ...form,
      suburb:   (form.suburb   || derived.suburb   || "").trim(),
      postcode: (form.postcode || derived.postcode || "").trim(),
    };
    setSubmitting(true);
    setSubmittingMode(mode);
    try {
      const { data } = await api.post("/property-manager/properties", payload);
      if (data?.created === false) {
        toast.info("That property is already in your portfolio.");
      } else {
        toast.success("Property added to your managed portfolio.");
      }
      if (mode === "add-and-book") {
        // Phase 2L-A121.B — create-first, then book hand-off.
        onCreatedAndBook?.(data?.property);
      } else {
        onCreated?.(data?.property);
      }
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not add property — please try again.";
      setError(typeof msg === "string" ? msg : "Could not add property — please try again.");
    } finally {
      setSubmitting(false);
      setSubmittingMode(null);
    }
  };

  return (
    <div
      data-testid="pm-add-property-modal"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/55 backdrop-blur-sm p-0 sm:p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="w-full sm:max-w-2xl bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col max-h-[92vh]">
        <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-slate-100">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-700">Add managed property</div>
            <h2 className="font-display text-lg sm:text-xl font-bold text-slate-900 mt-1">New entry in your portfolio</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="pm-add-property-close"
            className="p-2 -mr-2 rounded-lg text-slate-400 hover:text-slate-900 hover:bg-slate-100"
            aria-label="Close add managed property dialog"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form
          onSubmit={submit}
          data-testid="pm-add-property-form"
          className="overflow-y-auto px-6 py-5 space-y-5"
        >
          <p className="text-xs text-slate-500 -mt-1">
            Adds the property to your <strong>Managed Properties</strong> list.
            No test, booking, payment, or score is created — you can book
            the first test from the new card.
          </p>

          {/* Address */}
          <div>
            <Label htmlFor="address" className="text-xs uppercase tracking-wider font-bold text-slate-500">Property address *</Label>
            <AddressAutocomplete
              value={form.address}
              onChange={(v) => set("address", v)}
              placeholder="123 Coastal Road, Noosa QLD"
              testId="pm-add-property-address"
            />
          </div>

          <div className="grid grid-cols-2 gap-3 hidden">
            {/* Suburb + Postcode UI removed — both fields are now
                derived from the Google Places autocomplete result at
                submit time. The hidden Inputs below keep the form
                state shape unchanged for any in-flight tests that
                still target these data-testids. */}
            <Input
              data-testid="pm-add-property-suburb"
              type="hidden"
              value={form.suburb}
              onChange={(e) => set("suburb", e.target.value)}
            />
            <Input
              data-testid="pm-add-property-postcode"
              type="hidden"
              value={form.postcode}
              onChange={(e) => set("postcode", e.target.value)}
            />
          </div>

          {/* Water source */}
          <div>
            <Label className="text-xs uppercase tracking-wider font-bold text-slate-500">Water source *</Label>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
              {WATER_SOURCES.map((opt) => {
                const active = form.water_source === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    data-testid={`pm-add-property-water-${opt.value}`}
                    onClick={() => set("water_source", opt.value)}
                    className={`px-3 py-2.5 rounded-xl text-xs font-semibold border transition-colors ${
                      active
                        ? "bg-cyan-600 border-cyan-600 text-white"
                        : "bg-white border-slate-200 text-slate-700 hover:border-cyan-300"
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Owner contact (optional) */}
          <fieldset className="rounded-2xl border border-slate-200 p-4">
            <legend className="px-2 text-[11px] uppercase tracking-wider font-bold text-slate-500">Owner contact (optional)</legend>
            <div className="grid sm:grid-cols-3 gap-3">
              <Input data-testid="pm-add-property-owner-name"  placeholder="Owner name"   value={form.owner_name}  onChange={(e) => set("owner_name", e.target.value)}  className="h-11 rounded-xl" />
              <Input data-testid="pm-add-property-owner-email" placeholder="Owner email"  value={form.owner_email} onChange={(e) => set("owner_email", e.target.value)} className="h-11 rounded-xl" type="email" />
              <Input data-testid="pm-add-property-owner-phone" placeholder="Owner phone"  value={form.owner_phone} onChange={(e) => set("owner_phone", e.target.value)} className="h-11 rounded-xl" />
            </div>
          </fieldset>

          {/* Tenant / on-site contact (optional) */}
          <fieldset className="rounded-2xl border border-slate-200 p-4">
            <legend className="px-2 text-[11px] uppercase tracking-wider font-bold text-slate-500">Tenant / on-site contact (optional)</legend>
            <div className="grid sm:grid-cols-3 gap-3">
              <Input data-testid="pm-add-property-tenant-name"  placeholder="Tenant name"  value={form.tenant_name}  onChange={(e) => set("tenant_name", e.target.value)}  className="h-11 rounded-xl" />
              <Input data-testid="pm-add-property-tenant-email" placeholder="Tenant email" value={form.tenant_email} onChange={(e) => set("tenant_email", e.target.value)} className="h-11 rounded-xl" type="email" />
              <Input data-testid="pm-add-property-tenant-phone" placeholder="Tenant phone" value={form.tenant_phone} onChange={(e) => set("tenant_phone", e.target.value)} className="h-11 rounded-xl" />
            </div>
          </fieldset>

          {/* Notes + cadence + testing required */}
          <div>
            <Label className="text-xs uppercase tracking-wider font-bold text-slate-500">Notes</Label>
            <Textarea
              data-testid="pm-add-property-notes"
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="Anything Aware should know — access, dogs, tank size, prior incidents…"
              className="mt-1.5 rounded-xl text-sm"
              rows={3}
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-500">Preferred retest cadence</Label>
              <select
                data-testid="pm-add-property-cadence"
                value={form.preferred_retest_cadence}
                onChange={(e) => set("preferred_retest_cadence", e.target.value)}
                className="mt-1.5 w-full h-11 rounded-xl border border-slate-200 px-3 text-sm bg-white"
              >
                {CADENCES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 mt-7 cursor-pointer">
              <input
                type="checkbox"
                data-testid="pm-add-property-testing-now"
                checked={form.testing_required_now}
                onChange={(e) => set("testing_required_now", e.target.checked)}
                className="w-4 h-4 rounded border-slate-300 text-cyan-600"
              />
              <span className="text-sm text-slate-700">Testing required now</span>
            </label>
          </div>

          {error && (
            <div data-testid="pm-add-property-error" className="flex items-start gap-2 rounded-xl bg-rose-50 border border-rose-200 px-3.5 py-2.5 text-sm text-rose-700">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </form>

        <div className="px-6 py-4 border-t border-slate-100 flex flex-col-reverse sm:flex-row sm:justify-end gap-2.5">
          <Button
            type="button"
            variant="outline"
            data-testid="pm-add-property-cancel"
            onClick={onClose}
            disabled={submitting}
            className="h-11 rounded-xl"
          >
            Cancel
          </Button>
          <Button
            type="button"
            data-testid="pm-add-property-submit"
            onClick={(e) => submit(e, "add")}
            disabled={submitting}
            variant="outline"
            className="h-11 rounded-xl"
          >
            {submitting && submittingMode === "add" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {submitting && submittingMode === "add" ? "Adding…" : "Add to portfolio"}
          </Button>
          <Button
            type="button"
            data-testid="pm-add-property-submit-and-book"
            onClick={(e) => submit(e, "add-and-book")}
            disabled={submitting}
            className="h-11 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white"
          >
            {submitting && submittingMode === "add-and-book" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {submitting && submittingMode === "add-and-book" ? "Adding…" : "Add & book first test →"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default PmAddPropertyModal;
